import { Scene, Mesh, Matrix, AbstractMesh, Camera } from "@babylonjs/core";
import { UniversalCamera, Nullable } from "babylonjs";

export class CSS3DObject extends Mesh {
  element: HTMLElement;

  constructor(element: HTMLElement, scene: Scene) {
      super("css3DObject", scene);
      this.element = element;
      this.element.style.position = 'absolute';
      this.element.style.pointerEvents = 'none';
      this.element.style.userSelect = 'none';
  }
}

export class CSS3DRenderer {
  domElement: HTMLDivElement;
  cameraElement: HTMLDivElement;
  width: number = 0;
  height: number = 0;
  widthHalf: number = 0;
  heightHalf: number = 0;
  cache: any;
  isIE: boolean;

  constructor() {
      var matrix = new Matrix();

      this.cache = {
          camera: { fov: 0, style: '' },
          objects: new WeakMap()
      };

      this.domElement = document.createElement('div');
      this.domElement.style.overflow = 'hidden';

      this.cameraElement = document.createElement('div');
      this.isIE =  /Edge/.test(navigator.userAgent) || /Edg/.test(navigator.userAgent);
      // this.isIE = (!!document['documentMode'] || /Edge/.test(navigator.userAgent) || /Edg/.test(navigator.userAgent));

      if (!this.isIE) {
          this.cameraElement.style.webkitTransformStyle = 'preserve-3d';
          this.cameraElement.style.transformStyle = 'preserve-3d';
      }
      this.cameraElement.style.pointerEvents = 'none';

      this.domElement.appendChild(this.cameraElement);
  }

  getSize() {
      return {
          width: this.width,
          height: this.height
      };
  }

  setSize(width: number, height: number) {
      this.width = width;
      this.height = height;
      this.widthHalf = this.width / 2;
      this.heightHalf = this.height / 2;

      this.domElement.style.width = `${width}px`;
      this.domElement.style.height = `${height}px`;

      this.cameraElement.style.width = `${width}px`;
      this.cameraElement.style.height = `${height}px`;
  }

  epsilon(value: number) {
      return Math.abs(value) < 1e-10 ? 0 : value;
  }

  getCameraCSSMatrix(matrix: Matrix) {
      const elements = matrix.m;

      return `matrix3d(${this.epsilon(elements[0])},${this.epsilon(-elements[1])},${this.epsilon(elements[2])},${this.epsilon(elements[3])},${this.epsilon(elements[4])},${this.epsilon(-elements[5])},${this.epsilon(elements[6])},${this.epsilon(elements[7])},${this.epsilon(elements[8])},${this.epsilon(-elements[9])},${this.epsilon(elements[10])},${this.epsilon(elements[11])},${this.epsilon(elements[12])},${this.epsilon(-elements[13])},${this.epsilon(elements[14])},${this.epsilon(elements[15])})`;
  }

  getObjectCSSMatrix(matrix: Matrix, cameraCSSMatrix: string) {
      const elements = matrix.m;
      const matrix3d = `matrix3d(${this.epsilon(elements[0])},${this.epsilon(elements[1])},${this.epsilon(elements[2])},${this.epsilon(elements[3])},${this.epsilon(-elements[4])},${this.epsilon(-elements[5])},${this.epsilon(-elements[6])},${this.epsilon(-elements[7])},${this.epsilon(elements[8])},${this.epsilon(elements[9])},${this.epsilon(elements[10])},${this.epsilon(elements[11])},${this.epsilon(elements[12])},${this.epsilon(elements[13])},${this.epsilon(elements[14])},${this.epsilon(elements[15])})`;

      if (this.isIE) {
          return `translate(-50%,-50%)translate(${this.widthHalf}px,${this.heightHalf}px)${cameraCSSMatrix}${matrix3d}`;
      }
      return `translate(-50%,-50%)${matrix3d}`;
  }

  renderObject(object: AbstractMesh | Scene, scene: Scene, camera: Camera, cameraCSSMatrix: string) {
      if (object instanceof CSS3DObject) {
          let style: string;
          let objectMatrixWorld = object.getWorldMatrix().clone();
          let camMatrix = camera.getWorldMatrix().toArray();
          let innerMatrix = objectMatrixWorld.clone().toArray();

          const youtubeVideoWidth = 4.8;
          const youtubeVideoHeight = 3.6;

          innerMatrix[0] *= -0.01 / youtubeVideoWidth
          innerMatrix[2] *= 0.01 / youtubeVideoWidth
          innerMatrix[5] *= 0.01 / youtubeVideoHeight
          innerMatrix[1] *= 0.01 / youtubeVideoWidth
          innerMatrix[6] *= 0.01 / youtubeVideoHeight
          innerMatrix[4] *= 0.01 / youtubeVideoHeight
                
          // Set position from camera
          innerMatrix[12] = -camMatrix[12] + object.position.x
          innerMatrix[13] = -camMatrix[13] + object.position.y
          innerMatrix[14] = camMatrix[14] - object.position.z
          innerMatrix[15] = camMatrix[15] * 0.00001
    
          objectMatrixWorld = Matrix.FromArray(innerMatrix)
                objectMatrixWorld = objectMatrixWorld.scale(100)
          style = this.getObjectCSSMatrix( objectMatrixWorld, cameraCSSMatrix)
                var element = object.element
                var cachedObject = this.cache.objects.get( object )
    
                if ( cachedObject === undefined || cachedObject.style !== style ) {
    
                    element.style.webkitTransform = style
                    element.style.transform = style
    
                    var objectData = { style: style }
    
                    this.cache.objects.set( object, objectData )
                }
                if ( element.parentNode !== this.cameraElement ) {
                    this.cameraElement.appendChild( element )
                }
    
            } else if ( object instanceof Scene ) {
                for ( var i = 0, l = object.meshes.length; i < l; i ++ ) {
                    this.renderObject( object.meshes[ i ], scene, camera, cameraCSSMatrix )
                }
            }
    }

    render(scene: Scene, camera: Nullable<Camera>) {
      if (camera === null) return;
      const projectionMatrix = camera.getProjectionMatrix()
      const fov = projectionMatrix.m[5] * this.heightHalf

      if (this.cache.camera.fov !== fov) {

        if (camera.mode == Camera.PERSPECTIVE_CAMERA ) {
          this.domElement.style.webkitPerspective = fov + 'px'
          this.domElement.style.perspective = fov + 'px'
        } else {
          this.domElement.style.webkitPerspective = ''
          this.domElement.style.perspective = ''
        }
        this.cache.camera.fov = fov
      }

      if ( camera.parent === null ) camera.computeWorldMatrix()

      const matrixWorld = camera.getWorldMatrix().clone()
      const rotation = matrixWorld.clone().getRotationMatrix().transpose()
      const innerMatrix = matrixWorld.clone().toArray();

      innerMatrix[1] = rotation.m[1]
      innerMatrix[2] = -rotation.m[2]
      innerMatrix[4] = -rotation.m[4]
      innerMatrix[6] = -rotation.m[6]
      innerMatrix[8] = -rotation.m[8]
      innerMatrix[9] = -rotation.m[9]

      const matrixWorld2 = Matrix.FromArray(innerMatrix)

      const cameraCSSMatrix = 'translateZ(' + fov + 'px)' + this.getCameraCSSMatrix( matrixWorld2 )

      const style = cameraCSSMatrix + 'translate(' + this.widthHalf + 'px,' + this.heightHalf + 'px)'

      if (this.cache.camera.style !== style && !this.isIE ) {
        this.cameraElement.style.webkitTransform = style
        this.cameraElement.style.transform = style
        this.cache.camera.style = style
      }

      this.renderObject(scene, scene, camera, cameraCSSMatrix )
    }

}


